'use client';

import { Field, Form, Formik, type FormikHelpers } from 'formik';
import { object, string } from 'yup';
import {
  InteractionApiError,
  isTerminalInteractionError,
  reportInteractionFailure,
  requestMagicLink,
} from '@/app/utils/auth-interactions-api';
import type { InteractionTerminalHandler } from './types';
import {
  FormIntro,
  FormSwitchButton,
  InputFieldset,
  TurnstileField,
  formErrorMessage,
  turnstileSiteKey,
  turnstileTheme,
  useTurnstileReset,
} from './shared-fields';

interface MagicLinkRequestValues {
  base: string;
  email: string;
  turnstile: string;
}

const magicLinkRequestValidationSchema = object().shape({
  email: string().email('Invalid email').required('Required'),
  turnstile: string().required('Please complete the CAPTCHA'),
});

export default function MagicLinkRequestForm({
  interactionToken,
  onRequested,
  onTerminal,
  onSwitchToLogin,
  onSwitchToSignup,
  hideHeader = false,
}: {
  interactionToken: string;
  onRequested: (email: string) => void;
  onTerminal: InteractionTerminalHandler;
  onSwitchToLogin: () => void;
  onSwitchToSignup: () => void;
  hideHeader?: boolean;
}) {
  const { turnstileKey, resetTurnstile } = useTurnstileReset();

  function resetSubmittedTurnstile(actions: FormikHelpers<MagicLinkRequestValues>) {
    actions.setFieldValue('turnstile', '', false);
    resetTurnstile();
  }

  return (
    <>
      {!hideHeader && (
        <FormIntro title="Email me a login link">
          <p>We&apos;ll send a secure link to your email address.</p>
          <p>
            Prefer your password?{' '}
            <FormSwitchButton onClick={onSwitchToLogin}>Log in here.</FormSwitchButton>
          </p>
        </FormIntro>
      )}

      <Formik
        initialValues={{
          base: '',
          email: '',
          turnstile: '',
        }}
        validationSchema={magicLinkRequestValidationSchema}
        onSubmit={async (values, actions: FormikHelpers<MagicLinkRequestValues>) => {
          try {
            await requestMagicLink({
              interactionToken,
              email: values.email,
              turnstile: values.turnstile,
            });
            onRequested(values.email);
          } catch (error) {
            actions.setSubmitting(false);
            resetSubmittedTurnstile(actions);
            if (error instanceof InteractionApiError) {
              if (isTerminalInteractionError(error)) {
                onTerminal(error);
                return;
              }
              actions.setErrors({ base: formErrorMessage(error) });
              return;
            }
            reportInteractionFailure(error, {
              source: 'magic-link-request-form',
              endpoint: '/api/auth/interactions/magic_link/request',
              operation: 'submit',
            });
            actions.setErrors({
              base: 'Magic link request failed: Please try again.',
            });
          }
        }}
      >
        {({ isSubmitting, isValid, dirty, errors }) => (
          <Form>
            {errors.base && <div className="form-error">{errors.base}</div>}
            <fieldset className="max-w-sm md:max-w-full md:w-lg flex flex-col space-y-4 mb-4">
              <InputFieldset
                name="email"
                label="Email"
                type="email"
                placeholder="eg. you@example.com"
                autoComplete="username"
              />
            </fieldset>
            <fieldset className="max-w-sm md:w-lg flex flex-col space-y-4 mb-6">
              <Field
                name="turnstile"
                component={TurnstileField}
                siteKey={turnstileSiteKey}
                theme={turnstileTheme}
                widgetKey={turnstileKey}
                action="magic_link_request"
                onWidgetError={(error: unknown) =>
                  reportInteractionFailure(error, {
                    source: 'magic-link-request-form',
                    endpoint: 'turnstile',
                    operation: 'widget',
                  })
                }
              />
            </fieldset>
            <div className="mb-4">
              <button
                type="submit"
                className="btn text-white bg-blue-500 hover:bg-blue-700 w-full"
                disabled={isSubmitting || !isValid || !dirty}
              >
                Send login link
              </button>
            </div>
            <div className="text-sm flex flex-col sm:flex-row sm:justify-between gap-2">
              <button
                type="button"
                onClick={onSwitchToLogin}
                className="text-left underline text-slate-400 hover:text-slate-700 font-medium cursor-pointer"
              >
                Use your password instead
              </button>
              <button
                type="button"
                onClick={onSwitchToSignup}
                className="text-left underline text-slate-400 hover:text-slate-700 font-medium cursor-pointer"
              >
                Create a new account
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
}
